import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import ClearDataButton from '../components/ClearDataButton';

function AccountSettingsPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [originalFirstName, setOriginalFirstName] = useState('');
  const [originalLastName, setOriginalLastName] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch user data when component mounts
  useEffect(() => {
    if (currentUser) {
      fetchUserData();
    }
  }, [currentUser]);

  const fetchUserData = async () => {
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setFirstName(userData.firstName || '');
        setLastName(userData.lastName || '');
        setOriginalFirstName(userData.firstName || '');
        setOriginalLastName(userData.lastName || '');
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setError('Failed to load user data');
    }
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    setIsSaving(true);

    try {
      // Update user document in Firestore
      const userRef = doc(db, 'users', currentUser.uid);
      const displayName = firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || null;
      
      await updateDoc(userRef, {
        firstName: firstName || null,
        lastName: lastName || null,
        displayName: displayName,
        lastUpdated: new Date().toISOString()
      });

      setOriginalFirstName(firstName);
      setOriginalLastName(lastName);
      setIsEditing(false);
      setSuccess('Profile updated successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFirstName(originalFirstName);
    setLastName(originalLastName);
    setIsEditing(false);
    setError('');
  };

  const handleBack = () => {
    navigate('/');
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      padding: '20px',
      paddingTop: '80px' // Space for navigation
    }}>
      <div style={{
        maxWidth: '600px',
        margin: '0 auto',
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '30px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '30px'
        }}>
          <button
            onClick={handleBack}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              marginRight: '15px',
              padding: '8px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0,0,0,0.05)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
          
          <h1 style={{
            margin: 0,
            fontSize: '1.8rem',
            fontWeight: '600',
            color: '#333'
          }}>Account Settings</h1>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div style={{
            backgroundColor: '#f8d7da',
            color: '#721c24',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '20px',
            fontSize: '0.9rem'
          }}>
            {error}
          </div>
        )}
        
        {success && (
          <div style={{
            backgroundColor: '#d4edda',
            color: '#155724',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '20px',
            fontSize: '0.9rem'
          }}>
            {success}
          </div>
        )}

        {/* Account Information */}
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{
            fontSize: '1.3rem',
            fontWeight: '600',
            marginBottom: '20px',
            color: '#555',
            borderBottom: '2px solid #f0f0f0',
            paddingBottom: '10px'
          }}>Account Information</h2>
          
          <div style={{
            display: 'grid',
            gap: '20px'
          }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.9rem',
                color: '#666',
                marginBottom: '8px',
                fontWeight: '500'
              }}>Email Address</label>
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                fontSize: '1rem',
                color: '#333',
                border: '1px solid #e9ecef'
              }}>
                {currentUser?.email}
              </div>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '0.9rem',
                color: '#666',
                marginBottom: '8px',
                fontWeight: '500'
              }}>Account ID</label>
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                fontSize: '0.85rem',
                color: '#333',
                fontFamily: 'monospace',
                border: '1px solid #e9ecef',
                wordBreak: 'break-all'
              }}>
                {currentUser?.uid}
              </div>
            </div>
          </div>
        </div>

        {/* Personal Information */}
        <div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h2 style={{
              fontSize: '1.3rem',
              fontWeight: '600',
              color: '#555',
              margin: 0,
              borderBottom: '2px solid #f0f0f0',
              paddingBottom: '10px',
              flex: 1
            }}>Personal Information</h2>
            
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                style={{
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  padding: '8px 20px',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease',
                  marginLeft: '20px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#0056b3'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#007bff'}
              >
                Edit Profile
              </button>
            )}
          </div>

          <div style={{
            display: 'grid',
            gap: '20px'
          }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.9rem',
                color: '#666',
                marginBottom: '8px',
                fontWeight: '500'
              }}>First Name</label>
              {isEditing ? (
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#007bff'}
                  onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                  placeholder="Enter your first name"
                />
              ) : (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  color: firstName ? '#333' : '#999',
                  border: '1px solid #e9ecef',
                  minHeight: '20px'
                }}>
                  {firstName || 'Not set'}
                </div>
              )}
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '0.9rem',
                color: '#666',
                marginBottom: '8px',
                fontWeight: '500'
              }}>Last Name</label>
              {isEditing ? (
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#007bff'}
                  onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                  placeholder="Enter your last name"
                />
              ) : (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  color: lastName ? '#333' : '#999',
                  border: '1px solid #e9ecef',
                  minHeight: '20px'
                }}>
                  {lastName || 'Not set'}
                </div>
              )}
            </div>

            {/* Action buttons when editing */}
            {isEditing && (
              <div style={{
                display: 'flex',
                gap: '12px',
                marginTop: '10px'
              }}>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  style={{
                    flex: 1,
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '500',
                    cursor: isSaving ? 'not-allowed' : 'pointer',
                    opacity: isSaving ? 0.7 : 1,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSaving) e.target.style.backgroundColor = '#218838';
                  }}
                  onMouseLeave={(e) => {
                    if (!isSaving) e.target.style.backgroundColor = '#28a745';
                  }}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
                
                <button
                  onClick={handleCancel}
                  disabled={isSaving}
                  style={{
                    flex: 1,
                    backgroundColor: 'transparent',
                    color: '#6c757d',
                    border: '2px solid #6c757d',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#6c757d';
                    e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#6c757d';
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Class Management Section */}
        <div style={{ marginTop: '40px' }}>
          <h2 style={{
            fontSize: '1.3rem',
            fontWeight: '600',
            marginBottom: '20px',
            color: '#555',
            borderBottom: '2px solid #f0f0f0',
            paddingBottom: '10px'
          }}>Class Management</h2>
          
          <div style={{
            backgroundColor: '#e3f2fd',
            border: '1px solid #90caf9',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px'
          }}>
            <p style={{
              margin: '0 0 16px 0',
              fontSize: '0.95rem',
              color: '#1565c0'
            }}>
              Create and manage classes, invite members, and control class permissions.
            </p>
            
            <button
              onClick={() => navigate('/class-setup')}
              style={{
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                padding: '10px 24px',
                borderRadius: '6px',
                fontSize: '0.95rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#1565c0'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#1976d2'}
            >
              Class Setup
            </button>
          </div>
        </div>

        {/* Data Management Section */}
        <div style={{ marginTop: '40px' }}>
          <h2 style={{
            fontSize: '1.3rem',
            fontWeight: '600',
            marginBottom: '20px',
            color: '#555',
            borderBottom: '2px solid #f0f0f0',
            paddingBottom: '10px'
          }}>Data Management</h2>
          
          <div style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeeba',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px'
          }}>
            <p style={{
              margin: '0 0 16px 0',
              fontSize: '0.95rem',
              color: '#856404'
            }}>
              <strong>Warning:</strong> This action will permanently delete all your data including events, TLDRs, and family settings. This cannot be undone.
            </p>
            
            <ClearDataButton 
              onDataCleared={() => {
                // Navigate back to home after data is cleared
                navigate('/');
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccountSettingsPage;